<script setup>
import { useCampaignAvailability } from 'dashboard/composables/useCampaignAvailability';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  campaigns: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'viewDetails',
  'pause',
  'resume',
  'stop',
]);

const { t } = useI18n();

const STATUS_COLORS = {
  active: 'bg-n-blue-3 text-n-blue-11',
  processing: 'bg-n-brand/10 text-n-brand',
  paused: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-teal-3 text-n-teal-11',
  stopped: 'bg-n-slate-3 text-n-slate-11',
};

const STATUS_LABELS = {
  active: 'CAMPAIGN.LIST.STATUS.SCHEDULED',
  processing: 'CAMPAIGN.LIST.STATUS.PROCESSING',
  paused: 'CAMPAIGN.LIST.STATUS.PAUSED',
  completed: 'CAMPAIGN.LIST.STATUS.COMPLETED',
  stopped: 'CAMPAIGN.LIST.STATUS.STOPPED',
};

const formatDate = timestamp => {
  if (!timestamp) return '-';
  const date = new Date(
    typeof timestamp === 'number' ? timestamp * 1000 : timestamp
  );
  const month = date.toLocaleDateString(undefined, { month: 'short' });
  const day = date.getDate();
  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${month} ${day}, ${time}`;
};

const getSentCount = campaign => {
  return Math.max(
    (campaign.processed_contacts || 0) - (campaign.failed_contacts || 0),
    0
  );
};

const performance = campaign => {
  const total = campaign.total_contacts || 0;
  if (total === 0) return { percent: 0, label: '0%' };
  const sent = getSentCount(campaign);
  const pct = Math.round((sent / total) * 100);
  return { percent: pct, label: `${pct}%` };
};

const volumeLabel = campaign => {
  const total = campaign.total_contacts || 0;
  return `${getSentCount(campaign)}/${total}`;
};

const volumeSublabel = campaign => {
  const status = campaign.campaign_status;
  if (status === 'completed' || status === 'stopped')
    return t('CAMPAIGN.LIST.VOLUME.SENT');
  if (status === 'processing') return t('CAMPAIGN.LIST.VOLUME.SENDING');
  return t('CAMPAIGN.LIST.VOLUME.SCHEDULED');
};

const isProcessing = campaign => campaign.campaign_status === 'processing';
const isPaused = campaign => campaign.campaign_status === 'paused';
const canEdit = campaign => campaign.campaign_status === 'active';
const canStop = campaign => isProcessing(campaign) || isPaused(campaign);

const performanceBarColor = campaign => {
  const failed = campaign.failed_contacts || 0;
  const total = campaign.total_contacts || 0;
  if (total > 0 && failed === total) return 'bg-n-ruby-9';
  if (failed > 0) return 'bg-n-amber-9';
  return 'bg-n-teal-9';
};
const { campaignsEnabled } = useCampaignAvailability();
</script>

<template>
  <div class="w-full overflow-hidden rounded-lg border border-n-weak">
    <table class="w-full table-fixed">
      <colgroup>
        <col class="w-[35%]" />
        <col class="w-[12%]" />
        <col class="w-[18%]" />
        <col class="w-[10%]" />
        <col class="w-[13%]" />
        <col class="w-[12%]" />
      </colgroup>
      <thead>
        <tr class="bg-n-alpha-2">
          <th
            class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ t('CAMPAIGN.LIST.COLUMNS.DETAILS') }}
          </th>
          <th
            class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ t('CAMPAIGN.LIST.COLUMNS.STATUS') }}
          </th>
          <th
            class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ t('CAMPAIGN.LIST.COLUMNS.PERFORMANCE') }}
          </th>
          <th
            class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ t('CAMPAIGN.LIST.COLUMNS.VOLUME') }}
          </th>
          <th
            class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide whitespace-nowrap"
          >
            {{ t('CAMPAIGN.LIST.COLUMNS.DATE') }}
          </th>
          <th
            class="px-4 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ t('CAMPAIGN.LIST.COLUMNS.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="campaign in campaigns"
          :key="campaign.id"
          class="border-t border-n-weak hover:bg-n-alpha-1 transition-colors cursor-pointer"
          @click="emit('viewDetails', campaign)"
        >
          <!-- Campaign Details -->
          <td class="px-4 py-3">
            <div class="flex items-center gap-3">
              <span
                class="i-lucide-megaphone size-5 text-n-slate-10 shrink-0"
              />
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ campaign.title }}
                </span>
                <span class="text-xs text-n-slate-10 truncate">
                  {{ campaign.inbox?.name || '-' }}
                </span>
              </div>
            </div>
          </td>

          <!-- Status -->
          <td class="px-4 py-3">
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide"
              :class="
                STATUS_COLORS[campaign.campaign_status] || STATUS_COLORS.active
              "
            >
              {{
                t(
                  STATUS_LABELS[campaign.campaign_status] ||
                    STATUS_LABELS.active
                )
              }}
            </span>
          </td>

          <!-- Performance -->
          <td class="px-4 py-3">
            <div class="flex items-center gap-2 w-32">
              <div
                class="flex-1 h-1.5 rounded-full bg-n-alpha-3 overflow-hidden"
              >
                <div
                  class="h-full rounded-full transition-all duration-300"
                  :class="performanceBarColor(campaign)"
                  :style="{ width: `${performance(campaign).percent}%` }"
                />
              </div>
              <span class="text-xs font-medium text-n-slate-12 w-10 text-right">
                {{ performance(campaign).label }}
              </span>
            </div>
          </td>

          <!-- Volume -->
          <td class="px-4 py-3">
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">
                {{ volumeLabel(campaign) }}
              </span>
              <span class="text-[10px] text-n-slate-10">
                {{ volumeSublabel(campaign) }}
              </span>
            </div>
          </td>

          <!-- Date -->
          <td class="px-4 py-3">
            <span class="text-xs text-n-slate-11 whitespace-nowrap">
              {{ formatDate(campaign.scheduled_at || campaign.created_at) }}
            </span>
          </td>

          <!-- Actions -->
          <td class="px-4 py-3">
            <div class="flex items-center justify-end gap-1" @click.stop>
              <Button
                v-if="isProcessing(campaign)"
                variant="faded"
                size="xs"
                color="amber"
                icon="i-lucide-pause"
                @click="emit('pause', campaign)"
              />
              <Button
                v-if="isPaused(campaign)"
                variant="faded"
                size="xs"
                color="blue"
                icon="i-lucide-play"
                :disabled="!campaignsEnabled"
                @click="emit('resume', campaign)"
              />
              <Button
                v-if="canStop(campaign)"
                v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP')"
                variant="faded"
                size="xs"
                color="slate"
                icon="i-lucide-circle-stop"
                @click="emit('stop', campaign)"
              />
              <Button
                v-if="canEdit(campaign)"
                variant="faded"
                size="xs"
                color="slate"
                icon="i-lucide-pencil"
                :disabled="!campaignsEnabled"
                @click="emit('edit', campaign)"
              />
              <Button
                variant="faded"
                size="xs"
                color="ruby"
                icon="i-lucide-trash"
                @click="emit('delete', campaign)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
